import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  Tooltip,
} from '@mui/material';
import SellIcon from '@mui/icons-material/Sell';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { fetchWithAuth } from '../../services/api';

const SEARCH_MODES = {
  TAG: 'tagId',
  REQUEST_ID: 'requestId',
  COMPANY: 'company',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const MIN_PREFIX_TAG = 7;
const MIN_PREFIX_REQUEST = 7;

export default function UploadDocumentsToolsTackles({ onBack }) {
  const [searchMode, setSearchMode] = useState(SEARCH_MODES.TAG);

  // Tag Id mode
  const [tagTyped, setTagTyped] = useState('');

  // RequestId mode
  const [requestId, setRequestId] = useState('');
  const [requestTagOptions, setRequestTagOptions] = useState([]); // raw response for request-id mode
  const [requestTagSelected, setRequestTagSelected] = useState('');

  // Company mode
  const [stateId, setStateId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [tagSelectedFromList, setTagSelectedFromList] = useState('');

  // Loaded row details (single row)
  const [loadedDetails, setLoadedDetails] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const [companySiteItems, setCompanySiteItems] = useState([]);
  const [isLoadingCompanySite, setIsLoadingCompanySite] = useState(false);
  const [companySiteError, setCompanySiteError] = useState('');

  const [stateList, setStateList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  // Docs upload + list
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadIsLocked, setUploadIsLocked] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [docsList, setDocsList] = useState([]);
  const [docsActionMsg, setDocsActionMsg] = useState('');
  const [docLockBusyId, setDocLockBusyId] = useState(null);
  const [docDeleteBusyId, setDocDeleteBusyId] = useState(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);

  const filteredCompanies = useMemo(() => {
    if (!stateId) return [];
    return (companies || []).filter((c) => {
      const sid =
        c?.stateId ??
        c?.state?.stateId ??
        c?.state?.id ??
        c?.state_id ??
        c?.stateID;
      return sid != null && String(sid) === String(stateId);
    });
  }, [companies, stateId]);

  // Flatten by-company-site "items" (each with its own tagIds array) into one selectable list.
  const companySiteTagOptions = useMemo(() => {
    if (!siteId) return [];
    const options = [];
    (companySiteItems || []).forEach((item) => {
      (item.tagIds || []).forEach((tagId) => {
        options.push({ tagId, item });
      });
    });
    return options;
  }, [siteId, companySiteItems]);

  const loadStates = async () => {
    setIsLoadingStates(true);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/state-list`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to load states (${response.status})`);
      }
      const data = await response.json();
      setStateList(Array.isArray(data) ? data : []);
    } catch (e) {
      setStateList([]);
      setCompanySiteError(e?.message || 'Failed to load states.');
    } finally {
      setIsLoadingStates(false);
    }
  };

  const loadCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-companies-admin`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to load companies (${response.status})`);
      }
      const data = await response.json();
      const list = Array.isArray(data?.companies) ? data.companies : Array.isArray(data) ? data : [];
      setCompanies(list);
    } catch (e) {
      setCompanies([]);
      setCompanySiteError(e?.message || 'Failed to load companies.');
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const loadSites = async (companyIdParam) => {
    if (!companyIdParam) return;
    setIsLoadingSites(true);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-sites?companyId=${encodeURIComponent(companyIdParam)}`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to load sites (${response.status})`);
      }
      const data = await response.json();
      const list = Array.isArray(data?.sites) ? data.sites : Array.isArray(data) ? data : [];
      setSites(list);
    } catch (e) {
      setSites([]);
      setCompanySiteError(e?.message || 'Failed to load sites.');
    } finally {
      setIsLoadingSites(false);
    }
  };

  const getSelectedTagId = () => String(loadedDetails?.tagId || '').trim();
  const getSelectedMasterTagId = () => String(loadedDetails?.masterTagId || '').trim();

  const triggerPresignedDownload = async (url, fileName) => {
    if (!url) return;
    try {
      const resp = await fetch(url, { method: 'GET' });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = fileName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (_) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchDocsList = async (masterTagId) => {
    const id = String(masterTagId || '').trim();
    if (!id || id === '—') return [];
    setDocsLoading(true);
    setDocsError('');
    setDocsActionMsg('');
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/tools-tackles/tag-docs/list?masterTagId=${encodeURIComponent(id)}`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to fetch documents (${response.status})`);
      }
      const data = await response.json().catch(() => ({}));
      const docs = Array.isArray(data?.documents) ? data.documents : [];
      setDocsList(docs);
      return docs;
    } catch (e) {
      setDocsList([]);
      setDocsError(e?.message || 'Failed to load documents.');
      return [];
    } finally {
      setDocsLoading(false);
    }
  };

  const toggleDocLock = async (doc) => {
    if (!doc?.docId) return;
    const nextLocked = !Boolean(doc.locked);
    setDocsError('');
    setDocsActionMsg('');
    setDocLockBusyId(doc.docId);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/tools-tackles/tag-docs/lock-unlock?docId=${encodeURIComponent(doc.docId)}&isLocked=${encodeURIComponent(String(nextLocked))}`;
      const response = await fetchWithAuth(apiUrl, { method: 'PUT' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to update lock (${response.status})`);
      }
      const data = await response.json().catch(() => ({}));
      setDocsActionMsg(data?.message || 'Document lock updated successfully.');
      await fetchDocsList(getSelectedMasterTagId());
    } catch (e) {
      setDocsError(e?.message || 'Failed to update document lock.');
    } finally {
      setDocLockBusyId(null);
    }
  };

  const deleteDoc = async (doc) => {
    const masterTagId = getSelectedMasterTagId();
    if (!doc?.docId || !masterTagId || masterTagId === '—') return;
    setDocsError('');
    setDocsActionMsg('');
    setDocDeleteBusyId(doc.docId);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/tools-tackles/tag-docs/delete?docId=${encodeURIComponent(doc.docId)}&masterTagId=${encodeURIComponent(masterTagId)}`;
      const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to delete document (${response.status})`);
      }
      const data = await response.json().catch(() => ({}));
      setDocsActionMsg(data?.message || 'Document deleted successfully');
      setDeleteConfirmDoc(null);
      await fetchDocsList(masterTagId);
    } catch (e) {
      setDocsError(e?.message || 'Failed to delete document.');
    } finally {
      setDocDeleteBusyId(null);
    }
  };

  const handleConfirmDeleteDoc = () => {
    if (deleteConfirmDoc) deleteDoc(deleteConfirmDoc);
  };

  const openUploadDialog = () => {
    setUploadOpen(true);
    setUploadFile(null);
    setUploadIsLocked(true);
    setUploadError('');
    setUploadSuccess('');
  };

  const openViewDialog = async () => {
    setViewOpen(true);
    setDocsList([]);
    setDocsError('');
    setDocsActionMsg('');
    setDeleteConfirmDoc(null);
    const id = getSelectedMasterTagId();
    if (id) await fetchDocsList(id);
  };

  const handleUpload = async () => {
    const tagId = getSelectedTagId();
    const masterTagId = getSelectedMasterTagId();
    setUploadError('');
    setUploadSuccess('');

    if (!tagId) {
      setUploadError('Please load a Tag Id row first.');
      return;
    }
    if (!masterTagId || masterTagId === '—') {
      setUploadError('Master Tag Id is missing for the selected Tag Id.');
      return;
    }
    if (!uploadFile) {
      setUploadError('Please select a PDF file.');
      return;
    }
    if (uploadFile.type && uploadFile.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed.');
      return;
    }
    const maxBytes = 25 * 1024 * 1024;
    if (uploadFile.size > maxBytes) {
      setUploadError('File is too large. Max size is 25 MB.');
      return;
    }

    setIsUploading(true);
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/tools-tackles/tag-docs/upload`;
      const fd = new FormData();
      fd.append('masterTagId', masterTagId);
      fd.append('isLocked', String(Boolean(uploadIsLocked)));
      fd.append('file', uploadFile);

      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: fd,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Upload failed (${response.status})`);
      }
      const data = await response.json().catch(() => ({}));
      setUploadSuccess(data?.message || 'Document uploaded successfully.');
      setUploadFile(null);

      if (viewOpen) await fetchDocsList(masterTagId);
    } catch (e) {
      setUploadError(e?.message || 'Failed to upload.');
    } finally {
      setIsUploading(false);
    }
  };

  const mapApiDetailsToRow = (d, preferredTagId) => {
    if (!d) return null;
    const tagIds = Array.isArray(d.tagIds) ? d.tagIds : [];
    const tagId = preferredTagId || tagIds[0] || '—';
    return {
      tagId,
      tagIds,
      masterTagId: d.masterTagId ?? '—',
      requestId: d.requestId || '—',
      companyName: d.companyName || '—',
      siteName: d.siteName || '—',
      toolsTacklesName: d.toolsTacklesName || '—',
      status: d.adminApproval || '—',
      stateName: d.stateName || '—',
    };
  };

  const fetchDetailsByTagId = async (tagId) => {
    const apiUrl = `${API_BASE_URL}/api/v1/tagId/details/by-tagId?tagId=${encodeURIComponent(tagId)}`;
    const response = await fetchWithAuth(apiUrl, { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to fetch details (${response.status})`);
    }
    return await response.json();
  };

  const fetchDetailsByRequestId = async (rid) => {
    const apiUrl = `${API_BASE_URL}/api/v1/tagId/details/by-request-id?requestId=${encodeURIComponent(rid)}`;
    const response = await fetchWithAuth(apiUrl, { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to fetch details (${response.status})`);
    }
    return await response.json();
  };

  const fetchDetailsListByCompanySite = async (companyIdParam, siteIdParam) => {
    const apiUrl = `${API_BASE_URL}/api/v1/tagId/details/by-company-site?companyId=${encodeURIComponent(companyIdParam)}&siteId=${encodeURIComponent(siteIdParam)}`;
    const response = await fetchWithAuth(apiUrl, { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to fetch list (${response.status})`);
    }
    return await response.json();
  };

  const handleSelectMode = (_, v) => {
    if (!v) return;
    setSearchMode(v);
    setLoadedDetails(null);
    setInfoMessage('');
    setDetailsError('');
    setCompanySiteError('');
    setCompanySiteItems([]);
    setTagSelectedFromList('');
    setRequestTagOptions([]);
    setRequestTagSelected('');
  };

  const handlePickState = (v) => {
    setStateId(v);
    setCompanyId('');
    setSiteId('');
    setTagSelectedFromList('');
    setLoadedDetails(null);
    setSites([]);
    setCompanySiteItems([]);
    setCompanySiteError('');
    setInfoMessage('');
  };

  const handlePickCompany = (v) => {
    setCompanyId(v);
    setSiteId('');
    setTagSelectedFromList('');
    setLoadedDetails(null);
    setSites([]);
    setCompanySiteItems([]);
    setCompanySiteError('');
    setInfoMessage('');
    if (v) loadSites(v);
  };

  const handlePickSite = (v) => {
    setSiteId(v);
    setTagSelectedFromList('');
    setLoadedDetails(null);
    setCompanySiteItems([]);
    setCompanySiteError('');
    setInfoMessage('');
  };

  const handleSelectTagFromCompanyList = (tagId) => {
    setTagSelectedFromList(tagId);
    const option = companySiteTagOptions.find((x) => x.tagId === tagId) || null;
    setLoadedDetails(option ? mapApiDetailsToRow(option.item, tagId) : null);
  };

  const handleSelectTagFromRequestList = (tagId) => {
    setRequestTagSelected(tagId);
    setLoadedDetails(requestTagOptions ? mapApiDetailsToRow(requestTagOptions, tagId) : null);
  };

  // Auto-fetch by Tag Id (min characters)
  useEffect(() => {
    if (searchMode !== SEARCH_MODES.TAG) return undefined;
    const prefix = String(tagTyped || '').trim();
    setDetailsError('');
    setInfoMessage('');
    if (prefix.length < MIN_PREFIX_TAG) {
      setLoadedDetails(null);
      return undefined;
    }

    let alive = true;
    const t = setTimeout(async () => {
      try {
        setIsLoadingDetails(true);
        const data = await fetchDetailsByTagId(prefix);
        if (!alive) return;
        setLoadedDetails(mapApiDetailsToRow(data, prefix));
        setInfoMessage('');
      } catch (e) {
        if (!alive) return;
        setLoadedDetails(null);
        setDetailsError(e?.message || 'Failed to load details.');
      } finally {
        if (alive) setIsLoadingDetails(false);
      }
    }, 300);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [tagTyped, searchMode]);

  // Auto-fetch by Request Id (min characters) — response can contain multiple tag ids
  useEffect(() => {
    if (searchMode !== SEARCH_MODES.REQUEST_ID) return undefined;
    const prefix = String(requestId || '').trim();
    setDetailsError('');
    setInfoMessage('');
    setRequestTagSelected('');
    if (prefix.length < MIN_PREFIX_REQUEST) {
      setLoadedDetails(null);
      setRequestTagOptions([]);
      return undefined;
    }

    let alive = true;
    const t = setTimeout(async () => {
      try {
        setIsLoadingDetails(true);
        const data = await fetchDetailsByRequestId(prefix);
        if (!alive) return;
        setRequestTagOptions(data || null);
        const tagIds = Array.isArray(data?.tagIds) ? data.tagIds : [];
        if (tagIds.length === 1) {
          setRequestTagSelected(tagIds[0]);
          setLoadedDetails(mapApiDetailsToRow(data, tagIds[0]));
        } else {
          setLoadedDetails(null);
          if (tagIds.length === 0) setInfoMessage('No tag ids found for this request.');
        }
      } catch (e) {
        if (!alive) return;
        setLoadedDetails(null);
        setRequestTagOptions([]);
        setDetailsError(e?.message || 'Failed to load details.');
      } finally {
        if (alive) setIsLoadingDetails(false);
      }
    }, 300);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [requestId, searchMode]);

  // Load list for Company + Site
  useEffect(() => {
    if (searchMode !== SEARCH_MODES.COMPANY) return undefined;
    if (!isLoadingStates && (!stateList || stateList.length === 0)) loadStates();
    if (!isLoadingCompanies && (!companies || companies.length === 0)) loadCompanies();
    if (!companyId || !siteId) return undefined;

    let alive = true;
    const t = setTimeout(async () => {
      try {
        setIsLoadingCompanySite(true);
        setCompanySiteError('');
        setInfoMessage('');
        setCompanySiteItems([]);
        setTagSelectedFromList('');
        const resp = await fetchDetailsListByCompanySite(companyId, siteId);
        if (!alive) return;
        const items = Array.isArray(resp?.items) ? resp.items : [];
        setCompanySiteItems(items);
        if (Number(resp?.count || items.length) === 0) {
          setInfoMessage(resp?.message || 'No mapped Tag Id records found');
        }
      } catch (e) {
        if (!alive) return;
        setCompanySiteItems([]);
        setCompanySiteError(e?.message || 'Failed to load mapped Tag Id list.');
      } finally {
        if (alive) setIsLoadingCompanySite(false);
      }
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [companyId, siteId, searchMode]);

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2.5,
        px: 2.5,
        py: 1.05,
        bgcolor: '#0f172a',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <ArrowBackIcon sx={{ color: '#fff', fontSize: 20 }} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 800, color: '#fff', letterSpacing: '0.02em', lineHeight: 1.05 }}
        >
          Upload Documents - Tools &amp; Tackles
        </Typography>
      </Box>
      <Button
        onClick={onBack}
        variant="contained"
        size="small"
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          px: 2,
          py: 0.85,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 2,
          flexShrink: 0,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          '&:hover': { bgcolor: 'grey.100', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
        }}
      >
        Back to Worklists
      </Button>
    </Box>
  );

  return (
    <>
      {header}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="flex-start"
          >
            <ToggleButtonGroup
              value={searchMode}
              exclusive
              onChange={handleSelectMode}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                width: { xs: '100%', md: 'auto' },
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 0.6,
                boxShadow: '0 2px 10px rgba(15,23,42,0.08)',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 900,
                  px: 2.25,
                  py: 1.05,
                  border: 0,
                  borderRadius: 1.5,
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                  gap: 1,
                  minWidth: { xs: 0, md: 170 },
                  flex: { xs: 1, md: 'unset' },
                  justifyContent: 'center',
                  transition: 'all 0.18s ease',
                },
                '& .MuiToggleButton-root:hover': {
                  bgcolor: 'grey.50',
                  color: 'text.primary',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  boxShadow: 'inset 0 0 0 1px',
                  borderColor: 'primary.100',
                  border: '1px solid',
                },
              }}
            >
              <ToggleButton value={SEARCH_MODES.TAG}>
                <SellIcon sx={{ fontSize: 18 }} />
                Tag Id
              </ToggleButton>
              <ToggleButton value={SEARCH_MODES.REQUEST_ID}>
                <Typography component="span" sx={{ fontWeight: 900, lineHeight: 1 }}>
                  Request Id
                </Typography>
              </ToggleButton>
              <ToggleButton value={SEARCH_MODES.COMPANY}>
                <BusinessIcon sx={{ fontSize: 18 }} />
                Company
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>

        <Box sx={{ p: 2.5 }}>
          {searchMode === SEARCH_MODES.TAG && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tag Id"
                        placeholder={`e.g. AB/1236 (min ${MIN_PREFIX_TAG} characters)`}
                        value={tagTyped}
                        onChange={(e) => setTagTyped(e.target.value)}
                        inputProps={{ maxLength: 50 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SellIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setTagTyped('');
                          setLoadedDetails(null);
                          setDetailsError('');
                          setInfoMessage('');
                        }}
                        sx={{ textTransform: 'none', fontWeight: 900 }}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Stack>
                  {(detailsError || isLoadingDetails) && (
                    <Box sx={{ mt: 1.25 }}>
                      {isLoadingDetails && (
                        <Typography variant="caption" color="text.secondary" fontWeight={800}>
                          Loading details...
                        </Typography>
                      )}
                      {detailsError && (
                        <Typography variant="caption" color="error.main" fontWeight={800} sx={{ display: 'block' }}>
                          {detailsError}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Card>
            </Stack>
          )}

          {searchMode === SEARCH_MODES.REQUEST_ID && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Request Id"
                      placeholder={`Enter at least ${MIN_PREFIX_REQUEST} characters`}
                      value={requestId}
                      onChange={(e) => setRequestId(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" color="text.secondary" fontWeight={800}>#</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
                    <Button
                      variant="text"
                      onClick={() => {
                        setRequestId('');
                        setLoadedDetails(null);
                        setDetailsError('');
                        setInfoMessage('');
                        setRequestTagOptions([]);
                        setRequestTagSelected('');
                      }}
                      sx={{ textTransform: 'none', fontWeight: 800 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Stack>
                {(detailsError || isLoadingDetails) && (
                  <Box sx={{ mt: 1.25 }}>
                    {isLoadingDetails && (
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>
                        Loading details...
                      </Typography>
                    )}
                    {detailsError && (
                      <Typography variant="caption" color="error.main" fontWeight={800} sx={{ display: 'block' }}>
                        {detailsError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Card>

              {Array.isArray(requestTagOptions?.tagIds) && requestTagOptions.tagIds.length > 1 && (
                <Card variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary" fontWeight={800}>
                      This request has multiple tag ids. Select one to manage documents.
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel id="upload-tt-request-tag">Tag Id</InputLabel>
                      <Select
                        labelId="upload-tt-request-tag"
                        label="Tag Id"
                        value={requestTagSelected}
                        onChange={(e) => handleSelectTagFromRequestList(e.target.value)}
                      >
                        <MenuItem value="">Select tag id</MenuItem>
                        {requestTagOptions.tagIds.map((tid) => (
                          <MenuItem key={tid} value={tid}>{tid}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Card>
              )}
            </Stack>
          )}

          {searchMode === SEARCH_MODES.COMPANY && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth size="small" disabled={isLoadingStates}>
                    <InputLabel id="upload-tt-state">State</InputLabel>
                    <Select
                      labelId="upload-tt-state"
                      label="State"
                      value={stateId}
                      onChange={(e) => handlePickState(e.target.value)}
                    >
                      <MenuItem value="">Select state</MenuItem>
                      {stateList.map((s) => (
                        <MenuItem key={s.stateId} value={String(s.stateId)}>
                          {s.stateName} {s.stateCode ? `(${s.stateCode})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" disabled={!stateId || isLoadingCompanies}>
                    <InputLabel id="upload-tt-company">Company</InputLabel>
                    <Select
                      labelId="upload-tt-company"
                      label="Company"
                      value={companyId}
                      onChange={(e) => handlePickCompany(e.target.value)}
                    >
                      <MenuItem value="">Select company</MenuItem>
                      {filteredCompanies.map((c) => (
                        <MenuItem key={c.companyId || c.id} value={String(c.companyId || c.id)}>
                          {c.companyName || c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" disabled={!companyId || isLoadingSites}>
                    <InputLabel id="upload-tt-site">Site</InputLabel>
                    <Select
                      labelId="upload-tt-site"
                      label="Site"
                      value={siteId}
                      onChange={(e) => handlePickSite(e.target.value)}
                    >
                      <MenuItem value="">Select site</MenuItem>
                      {sites.map((s) => (
                        <MenuItem key={s.siteId || s.id} value={String(s.siteId || s.id)}>
                          {s.siteName || s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {(companySiteError || isLoadingStates || isLoadingCompanies || isLoadingSites) && (
                  <Box sx={{ mt: 1.25 }}>
                    {(isLoadingStates || isLoadingCompanies || isLoadingSites) && (
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>
                        Loading {isLoadingStates ? 'states' : isLoadingCompanies ? 'companies' : 'sites'}...
                      </Typography>
                    )}
                    {companySiteError && (
                      <Typography variant="caption" color="error.main" fontWeight={800} sx={{ display: 'block' }}>
                        {companySiteError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Card>

              <Card variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ width: { xs: '100%', md: 360 }, maxWidth: '100%' }}>
                    <FormControl fullWidth size="small" disabled={!siteId}>
                      <InputLabel id="upload-tt-company-tag">Tag Id</InputLabel>
                      <Select
                        labelId="upload-tt-company-tag"
                        label="Tag Id"
                        value={tagSelectedFromList}
                        onChange={(e) => handleSelectTagFromCompanyList(e.target.value)}
                      >
                        <MenuItem value="">
                          {siteId ? (isLoadingCompanySite ? 'Loading...' : 'Select tag id') : 'Select site first'}
                        </MenuItem>
                        {siteId && companySiteTagOptions.map((opt) => (
                          <MenuItem key={opt.tagId} value={opt.tagId}>
                            {opt.tagId} {opt.item?.toolsTacklesName ? `• ${opt.item.toolsTacklesName}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {siteId && !isLoadingCompanySite && companySiteTagOptions.length === 0 && (
                      <Typography variant="caption" color="text.secondary" fontWeight={650} sx={{ display: 'block', mt: 0.75 }}>
                        {infoMessage || 'No mapped Tag Id records found.'}
                      </Typography>
                    )}
                    {companySiteError && (
                      <Typography variant="caption" color="error.main" fontWeight={800} sx={{ display: 'block', mt: 0.75 }}>
                        {companySiteError}
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setTagSelectedFromList('');
                        setLoadedDetails(null);
                      }}
                      disabled={!tagSelectedFromList}
                      sx={{ textTransform: 'none', fontWeight: 900 }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: 2.25,
              borderStyle: loadedDetails ? 'solid' : 'dashed',
              bgcolor: loadedDetails ? 'background.paper' : 'grey.50',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={900}>
                  Loaded details
                </Typography>
              </Box>
              <Chip
                size="small"
                label={loadedDetails ? '1 row loaded' : 'No row loaded'}
                color={loadedDetails ? 'success' : 'default'}
                variant={loadedDetails ? 'filled' : 'outlined'}
                sx={{ fontWeight: 900 }}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {!loadedDetails ? (
              <Typography variant="body2" color="text.secondary" fontWeight={650}>
                Search using Tag Id / Request Id / Company flow to load details here.
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr 160px 1fr' }, gap: 1.25, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={900}>Tag Id</Typography>
                <Typography variant="body2" fontWeight={800}>{loadedDetails.tagId}</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={900}>Request Id</Typography>
                <Typography variant="body2" fontWeight={800}>{loadedDetails.requestId}</Typography>

                <Typography variant="body2" color="text.secondary" fontWeight={900}>State</Typography>
                <Typography variant="body2" fontWeight={700}>{loadedDetails.stateName}</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={900}>Approval</Typography>
                <Typography variant="body2" fontWeight={800}>{loadedDetails.status}</Typography>

                <Typography variant="body2" color="text.secondary" fontWeight={900}>Company</Typography>
                <Typography variant="body2" fontWeight={700}>{loadedDetails.companyName}</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={900}>Site</Typography>
                <Typography variant="body2" fontWeight={700}>{loadedDetails.siteName}</Typography>

                <Typography variant="body2" color="text.secondary" fontWeight={900}>Tools &amp; Tackles</Typography>
                <Typography variant="body2" fontWeight={700}>{loadedDetails.toolsTacklesName}</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={900}>Master Tag Id</Typography>
                <Typography variant="body2" fontWeight={700}>{loadedDetails.masterTagId}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Box />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  disabled={!loadedDetails}
                  variant="contained"
                  disableElevation
                  onClick={openUploadDialog}
                  startIcon={<UploadFileIcon />}
                  sx={{ textTransform: 'none', fontWeight: 900 }}
                >
                  Upload documents
                </Button>
                <Button
                  disabled={!loadedDetails}
                  variant="outlined"
                  onClick={openViewDialog}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', fontWeight: 900 }}
                >
                  View existing
                </Button>
              </Stack>
            </Stack>
          </Card>

          {/* Upload document dialog */}
          <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Upload document
              <IconButton onClick={() => setUploadOpen(false)} size="small" aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  Tag Id: <strong>{getSelectedTagId() || '—'}</strong>
                </Typography>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ textTransform: 'none', fontWeight: 900, alignSelf: 'flex-start' }}
                  disabled={isUploading}
                >
                  Choose PDF
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setUploadFile(f);
                      setUploadError('');
                      setUploadSuccess('');
                    }}
                  />
                </Button>

                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {uploadFile ? uploadFile.name : 'Only PDF • Max 25 MB'}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={uploadIsLocked}
                      onChange={(e) => setUploadIsLocked(Boolean(e.target.checked))}
                      disabled={isUploading}
                    />
                  }
                  label={<Typography variant="body2" fontWeight={800}>Lock document (recommended)</Typography>}
                />

                {uploadError && (
                  <Typography variant="body2" color="error.main" fontWeight={800}>
                    {uploadError}
                  </Typography>
                )}
                {uploadSuccess && (
                  <Typography variant="body2" color="success.main" fontWeight={800}>
                    {uploadSuccess}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.75 }}>
              <Button onClick={() => setUploadOpen(false)} variant="text" sx={{ textTransform: 'none', fontWeight: 900 }} disabled={isUploading}>
                Close
              </Button>
              <Button onClick={handleUpload} variant="contained" disableElevation sx={{ textTransform: 'none', fontWeight: 900 }} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* View existing documents dialog */}
          <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Existing documents
              <IconButton onClick={() => setViewOpen(false)} size="small" aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Tag Id: <strong>{getSelectedTagId() || '—'}</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    disableElevation
                    onClick={() => fetchDocsList(getSelectedMasterTagId())}
                    sx={{ textTransform: 'none', fontWeight: 900 }}
                    disabled={docsLoading || !getSelectedTagId()}
                  >
                    Refresh
                  </Button>
                </Stack>

                {docsLoading && (
                  <Typography variant="body2" color="text.secondary" fontWeight={800}>
                    Loading documents...
                  </Typography>
                )}
                {docsError && (
                  <Typography variant="body2" color="error.main" fontWeight={800}>
                    {docsError}
                  </Typography>
                )}
                {docsActionMsg && !docsError && (
                  <Typography variant="body2" color="success.main" fontWeight={800}>
                    {docsActionMsg}
                  </Typography>
                )}

                {!docsLoading && !docsError && docsList.length === 0 && (
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    No documents uploaded for this Tag Id.
                  </Typography>
                )}

                {!docsLoading && docsList.length > 0 && (
                  <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    {docsList.map((d) => (
                      <ListItemButton
                        key={d.docId}
                        sx={{ py: 1.25 }}
                        onClick={() => triggerPresignedDownload(d.downloadUrl, d.docName)}
                      >
                        <ListItemText
                          primary={<Typography fontWeight={900}>{d.docName || '—'}</Typography>}
                          secondary={
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              {d.locked ? 'Locked' : 'Unlocked'} • {d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}
                            </Typography>
                          }
                        />
                        <Stack direction="row" spacing={0.75} alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={d.locked ? 'Unlock' : 'Lock'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => toggleDocLock(d)}
                                disabled={docLockBusyId === d.docId || docDeleteBusyId === d.docId || docsLoading}
                                sx={{ textTransform: 'none', fontWeight: 900 }}
                              >
                                {docLockBusyId === d.docId ? 'Updating...' : d.locked ? 'Unlock' : 'Lock'}
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete document">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                onClick={() => setDeleteConfirmDoc(d)}
                                disabled={docDeleteBusyId === d.docId || docLockBusyId === d.docId || docsLoading}
                                sx={{ textTransform: 'none', fontWeight: 900 }}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton aria-label="Download" onClick={() => triggerPresignedDownload(d.downloadUrl, d.docName)}>
                              <CloudDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.75 }}>
              <Button onClick={() => setViewOpen(false)} variant="text" sx={{ textTransform: 'none', fontWeight: 900 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(deleteConfirmDoc)}
            onClose={() => !docDeleteBusyId && setDeleteConfirmDoc(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 900 }}>Delete document?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" fontWeight={700}>
                Are you sure you want to delete{' '}
                <strong>{deleteConfirmDoc?.docName || 'this document'}</strong>? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.75 }}>
              <Button
                onClick={() => setDeleteConfirmDoc(null)}
                variant="text"
                sx={{ textTransform: 'none', fontWeight: 900 }}
                disabled={Boolean(docDeleteBusyId)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDeleteDoc}
                variant="contained"
                color="error"
                disableElevation
                startIcon={<DeleteOutlineIcon />}
                sx={{ textTransform: 'none', fontWeight: 900 }}
                disabled={Boolean(docDeleteBusyId)}
              >
                {docDeleteBusyId ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
    </>
  );
}
